/**
 * Admin — Face Search events list (/face-search). Roles: admin, manager.
 * Renders inside the app's normal authenticated layout (sidebar etc.).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, QrCode, MessageCircle, ExternalLink, AlertTriangle, Images, Users, Search, RefreshCw, Palette, Upload, Trash2, Radio } from "lucide-react";
import { faceAdmin, errMsg, saveBlob } from "../lib/faceApi";
import { Btn, BrandChip, Field, Modal, ProgressBar, StatusChip, Toggle, inputCls, useToastState, usd } from "../lib/faceUi";

const SECRET_STEPS = [
  ["AWS_ACCESS_KEY_ID / AWS_SECRET_ACCESS_KEY", "AWS Console → IAM → Users → create user with AmazonRekognitionFullAccess → Create access key."],
  ["AWS_REGION", "Use ap-south-1 (Mumbai)."],
  ["GOOGLE_DRIVE_API_KEY", "Google Cloud Console → APIs & Services → enable Google Drive API → Credentials → Create API key."],
];

function SetupBanner({ config }) {
  if (!config || config.ready) return null;
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
        <div className="space-y-2 text-sm">
          <div className="font-semibold text-amber-200">Face Search needs {config.missing.length} secret{config.missing.length > 1 ? "s" : ""} before it can be used</div>
          <div className="text-white/70">Add these in Emergent → Manage → Secrets (fill both Live and Test), then reload this page:</div>
          <ul className="space-y-1">
            {config.missing.map((m) => (
              <li key={m} className="flex flex-wrap gap-x-2"><code className="rounded bg-black/40 px-1.5 py-0.5 text-amber-200">{m}</code>
                <span className="text-white/50">{(SECRET_STEPS.find(([k]) => k.includes(m)) || [])[1]}</span></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function NewEventModal({ open, onClose, onCreated, showToast }) {
  const [form, setForm] = useState({ brand: "wittyshadows", title: "", couple_names: "", event_date: "", drive_folder_url: "", pin: "", allow_download: true, allow_browse_all: false, require_guest_details: true, live: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.drive_folder_url.trim()) return showToast("Title and Drive folder link are required", "error");
    setBusy(true);
    try {
      const { live, ...rest } = form;
      const ev = await faceAdmin.create({ ...rest, pin: form.pin || null, event_date: form.event_date || null, live_sync_minutes: live ? 5 : 0 });
      onCreated(ev);
    } catch (err) {
      showToast(errMsg(err), "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="New face-search gallery">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Brand">
          <div className="grid grid-cols-2 gap-2">
            {[["wittyshadows", "Wittyshadows"], ["triobliss", "Trio Bliss"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => set("brand", k)} className={`rounded-lg border px-3 py-2 text-sm ${form.brand === k ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-white/10 bg-white/5 text-white/70"}`}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Event title"><input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Riya & Arjun — Wedding, Udaipur" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Couple names"><input className={inputCls} value={form.couple_names} onChange={(e) => set("couple_names", e.target.value)} placeholder="Riya & Arjun" /></Field>
          <Field label="Event date"><input type="date" className={inputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} /></Field>
        </div>
        <Field label="Google Drive folder link" hint='The folder must be shared as "Anyone with the link – Viewer". Sub-folders (Haldi, Mehendi, Day 1…) are included.'>
          <input className={inputCls} value={form.drive_folder_url} onChange={(e) => set("drive_folder_url", e.target.value)} placeholder="https://drive.google.com/drive/folders/…" />
        </Field>
        <Field label="PIN (optional)" hint="4–6 digits guests must enter. Leave empty for no PIN.">
          <input className={inputCls} inputMode="numeric" value={form.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="e.g. 2611" />
        </Field>
        <div className="space-y-2">
          <Toggle checked={form.require_guest_details} onChange={(v) => set("require_guest_details", v)} label="Ask guests for name & WhatsApp" hint="Saved as leads in Leads & Sales" />
          <Toggle checked={form.allow_download} onChange={(v) => set("allow_download", v)} label="Allow full-quality downloads" />
          <Toggle checked={form.allow_browse_all} onChange={(v) => set("allow_browse_all", v)} label="Let guests browse the full gallery" hint="Off = guests only see photos of themselves" />
          <Toggle checked={form.live} onChange={(v) => set("live", v)} label="Live event mode" hint="Re-scan the Drive folder every 5 min and index new photos automatically (turn off after the event)" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" loading={busy}>Read folder</Btn>
        </div>
      </form>
    </Modal>
  );
}

const BRAND_FIELDS = [
  ["display_name", "Studio name", "Witty Shadows"],
  ["tagline", "Tagline", "Destination wedding photography & films"],
  ["website_url", "Website (See our work)", "https://wittyshadows.com"],
  ["instagram_url", "Instagram", "@wittyshadows or full link"],
  ["whatsapp_number", "WhatsApp number (guests message this)", "98765 43210"],
  ["phone", "Phone (optional, for the saved contact)", ""],
  ["email", "Email (optional)", "info@wittyshadows.com"],
  ["cta_title", "Card title", "Loved these photos?"],
  ["cta_text", "Card text", "We'd love to shoot your celebration too…"],
];

function BrandSettingsModal({ open, onClose, showToast }) {
  const [brands, setBrands] = useState(null);
  const [brand, setBrand] = useState("wittyshadows");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cur = brands?.find((b) => b.brand === brand);
  useEffect(() => {
    if (!open) return;
    faceAdmin.brands().then((list) => { setBrands(list); }).catch((e) => showToast(errMsg(e), "error"));
  }, [open, showToast]);
  useEffect(() => {
    if (cur) setForm(Object.fromEntries(BRAND_FIELDS.map(([k]) => [k, cur[k] || ""]).concat([["primary_color", cur.primary_color || "#f5a623"], ["show_cta", cur.show_cta !== false]])));
  }, [cur]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updated = await faceAdmin.updateBrand(brand, form);
      setBrands((list) => list.map((b) => (b.brand === brand ? updated : b)));
      showToast("Brand settings saved — guests see them immediately", "success");
    } catch (err) { showToast(errMsg(err), "error"); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const updated = await faceAdmin.uploadLogo(brand, file);
      setBrands((list) => list.map((b) => (b.brand === brand ? updated : b)));
      showToast("Logo updated", "success");
    } catch (err) { showToast(errMsg(err), "error"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeLogo = async () => {
    setBusy(true);
    try { const updated = await faceAdmin.deleteLogo(brand); setBrands((list) => list.map((b) => (b.brand === brand ? updated : b))); } catch (err) { showToast(errMsg(err), "error"); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Brand & contact on guest pages" width="max-w-2xl">
      <div className="mb-4 grid grid-cols-2 gap-2">
        {[["wittyshadows", "Wittyshadows"], ["triobliss", "Trio Bliss"]].map(([k, l]) => (
          <button type="button" key={k} onClick={() => setBrand(k)} className={`rounded-lg border px-3 py-2 text-sm ${brand === k ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-white/10 bg-white/5 text-white/70"}`}>{l}</button>
        ))}
      </div>
      {!cur ? <div className="py-8 text-center text-white/50">Loading…</div> : (
        <form onSubmit={save} className="space-y-4">
          <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-black/30 p-3">
            <div className="flex h-16 w-40 items-center justify-center rounded-lg bg-[#0b0b0c]">
              {cur.logo_url ? <img src={cur.logo_url} alt="" className="max-h-14 max-w-[150px] object-contain" /> : <span className="text-xs text-white/40">No logo yet</span>}
            </div>
            <div className="space-y-1 text-xs text-white/60">
              <div className="text-sm font-medium text-white">Logo</div>
              <div>PNG with transparent background, white or light version works best on the dark guest page.</div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              <div className="flex gap-2 pt-1">
                <Btn type="button" variant="ghost" className="px-3 py-1.5 text-xs" loading={busy} onClick={() => fileRef.current?.click()}><Upload className="h-3.5 w-3.5" /> Upload logo</Btn>
                {cur.has_logo && <Btn type="button" variant="ghost" className="px-3 py-1.5 text-xs" onClick={removeLogo}><Trash2 className="h-3.5 w-3.5" /> Remove</Btn>}
              </div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {BRAND_FIELDS.map(([k, label, ph]) => (
              <Field key={k} label={label}><input className={inputCls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} /></Field>
            ))}
            <Field label="Accent colour" hint="Buttons and highlights on the guest page">
              <div className="flex gap-2">
                <input type="color" value={form.primary_color || "#f5a623"} onChange={(e) => set("primary_color", e.target.value)} className="h-10 w-14 cursor-pointer rounded-lg border border-white/10 bg-transparent" />
                <input className={inputCls} value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} placeholder="#f5a623" />
              </div>
            </Field>
          </div>
          <Toggle checked={form.show_cta !== false} onChange={(v) => set("show_cta", v)} label="Show the studio card to guests" hint="See our work · Instagram · WhatsApp us · Save our number" />
          <div className="flex justify-end gap-2 pt-1">
            <Btn variant="ghost" type="button" onClick={onClose}>Close</Btn>
            <Btn type="submit" loading={busy}>Save</Btn>
          </div>
        </form>
      )}
    </Modal>
  );
}

function EventCard({ ev, onOpen, showToast }) {
  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); showToast(msg, "success"); } catch (_) { window.prompt("Copy", text); }
  };
  const qr = async () => {
    try { saveBlob(await faceAdmin.qr(ev.id), `${ev.title.replace(/[^a-z0-9]+/gi, "-")}-qr.png`); } catch (err) { showToast(errMsg(err), "error"); }
  };
  const wa = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Find your photos from ${ev.couple_names || ev.title} — take a selfie here: ${ev.share_url}`)}`, "_blank", "noopener");
  const done = (ev.photos_indexed || 0) + (ev.photos_failed || 0);
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#141416]">
      <button onClick={onOpen} className="block w-full text-left">
        <div className="relative h-36 bg-zinc-900">
          {ev.cover_url && <img src={ev.cover_url} alt="" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#141416] to-transparent" />
          <div className="absolute left-3 top-3 flex flex-wrap gap-2"><StatusChip status={ev.status} active={ev.is_active} /><BrandChip brand={ev.brand} />{ev.live_sync_minutes > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-2.5 py-0.5 text-xs font-medium text-red-300"><Radio className="h-3 w-3" /> Live · {ev.live_sync_minutes} min</span>}</div>
        </div>
        <div className="p-4">
          <div className="truncate text-base font-semibold">{ev.title}</div>
          <div className="truncate text-sm text-white/60">{ev.couple_names}{ev.event_date ? ` · ${new Date(ev.event_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : ""}</div>
          <div className="mt-3">
            <div className="mb-1 flex justify-between text-xs text-white/60"><span>{done.toLocaleString("en-IN")} of {(ev.photos_total || 0).toLocaleString("en-IN")} photos indexed</span><span>{ev.progress_pct}%</span></div>
            <ProgressBar value={ev.progress_pct} />
          </div>
          <div className="mt-3 flex gap-4 text-xs text-white/60">
            <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ev.stats?.guests || 0} guests</span>
            <span className="inline-flex items-center gap-1"><Search className="h-3.5 w-3.5" /> {ev.stats?.searches || 0} searches</span>
            <span className="inline-flex items-center gap-1"><Images className="h-3.5 w-3.5" /> {ev.faces_indexed || 0} faces</span>
          </div>
        </div>
      </button>
      <div className="flex flex-wrap gap-1.5 border-t border-white/10 p-2">
        <Btn variant="ghost" className="px-2.5 py-1.5 text-xs" onClick={() => copy(ev.public_url, "Guest link copied")}><Copy className="h-3.5 w-3.5" /> Copy link</Btn>
        <Btn variant="ghost" className="px-2.5 py-1.5 text-xs" onClick={qr}><QrCode className="h-3.5 w-3.5" /> QR</Btn>
        <Btn variant="ghost" className="px-2.5 py-1.5 text-xs" onClick={wa}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</Btn>
        <Btn variant="ghost" className="ml-auto px-2.5 py-1.5 text-xs" onClick={onOpen}><ExternalLink className="h-3.5 w-3.5" /> Open</Btn>
      </div>
    </div>
  );
}

export default function FaceSearchPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [toast, showToast] = useToastState();
  const timer = useRef(null);

  const load = useCallback(async (silent = false) => {
    try {
      const [c, list] = await Promise.all([faceAdmin.config(), faceAdmin.list()]);
      setConfig(c);
      setEvents(list);
    } catch (err) {
      if (!silent) showToast(errMsg(err), "error");
      if (events === null) setEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const busy = (events || []).some((e) => e.status === "indexing" || e.status === "listing");
    clearInterval(timer.current);
    if (busy) timer.current = setInterval(() => load(true), 5000);
    return () => clearInterval(timer.current);
  }, [events, load]);

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 text-white sm:p-6">
      {toast}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Face Search</h1>
          <p className="text-sm text-white/60">AI galleries where guests find their own photos with a selfie.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn variant="ghost" onClick={() => load()}><RefreshCw className="h-4 w-4" /> Refresh</Btn>
          {config?.can_manage !== false && <Btn variant="ghost" onClick={() => setBrandOpen(true)}><Palette className="h-4 w-4" /> Brand & contact</Btn>}
          <Btn onClick={() => setOpen(true)} disabled={!config?.ready}><Plus className="h-4 w-4" /> New gallery</Btn>
        </div>
      </div>
      <SetupBanner config={config} />
      {events === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-white/5" />)}</div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center">
          <Images className="mx-auto h-10 w-10 text-white/30" />
          <div className="mt-3 font-semibold">No galleries yet</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-white/60">Share a wedding's Drive folder as "Anyone with the link", paste it here, and get a link + QR your guests can use to find their photos.</p>
          <Btn className="mt-4" onClick={() => setOpen(true)} disabled={!config?.ready}><Plus className="h-4 w-4" /> New gallery</Btn>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => <EventCard key={ev.id} ev={ev} showToast={showToast} onOpen={() => navigate(`/face-search/${ev.id}`)} />)}
        </div>
      )}
      {config?.ready && (
        <div className="text-xs text-white/40">Indexing costs about ${config.cost_per_photo_usd} per photo (Amazon Rekognition, {config.aws_region || "—"}, {config.index_concurrency} photos in parallel). A 5,000-photo wedding ≈ {usd(5000 * config.cost_per_photo_usd)}; each guest search costs the same as one photo.</div>
      )}
      <NewEventModal open={open} onClose={() => setOpen(false)} showToast={showToast} onCreated={(ev) => { setOpen(false); navigate(`/face-search/${ev.id}`); }} />
      <BrandSettingsModal open={brandOpen} onClose={() => setBrandOpen(false)} showToast={showToast} />
    </div>
  );
}
